import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography, Checkbox, IconButton, Alert, Chip, Skeleton, Button, Tooltip, Card, CardContent } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import LogoutIcon from '@mui/icons-material/Logout';
import ReactPlayer from 'react-player';
import { useAppleMusicPlaylist } from 'src/hooks/useAppleMusicPlaylist';
import { useAppleMusicAuth } from 'src/hooks/useAppleMusicAuth';
import { useAppleMusicPlayer } from 'src/hooks/useAppleMusicPlayer';

interface AppleMusicPlaylistFieldProps {
  playlistUrl: string;
  minChoices?: number;
  maxChoices?: number;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

const AppleMusicPlaylistField: React.FC<AppleMusicPlaylistFieldProps> = ({
  playlistUrl,
  minChoices,
  maxChoices,
  value,
  onChange,
  disabled
}) => {
  const { tracks, playlistMeta, isLoading, error } = useAppleMusicPlaylist(playlistUrl);
  const {
    isReady: isMusicKitReady,
    isAuthorized,
    authorize,
    unauthorize,
    isLoading: isAuthLoading
  } = useAppleMusicAuth();
  const { playingTrackId, isPlaying, playbackMode, playPause, handlePreviewEnded } = useAppleMusicPlayer({
    isAuthorized,
    isMusicKitReady
  });

  // Keep a ref that always points at the latest value so callbacks never go stale
  const valueRef = useRef(value);
  valueRef.current = value;

  const selectedSet = new Set(value);

  const handleToggle = useCallback(
    (trackId: string) => {
      if (disabled) return;
      // Always read from the ref so we never work with a stale selection
      const current = new Set(valueRef.current);
      if (current.has(trackId)) {
        current.delete(trackId);
      } else {
        if (maxChoices && current.size >= maxChoices) return;
        current.add(trackId);
      }
      onChange(Array.from(current));
    },
    [maxChoices, onChange, disabled]
  );

  const previewTrack = playbackMode === 'preview' ? tracks.find((t) => t.id === playingTrackId) : null;

  // ---- Loading state ----
  if (isLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        {[...Array(5)].map((_, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
            <Skeleton variant="rounded" width={48} height={48} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" height={20} />
              <Skeleton width="40%" height={16} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  // ---- Error state ----
  if (error) {
    return (
      <Alert severity="error" sx={{ width: '100%' }}>
        {error}
      </Alert>
    );
  }

  // ---- Empty state ----
  if (tracks.length === 0) {
    return (
      <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
        <MusicNoteIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No tracks found in this playlist.
        </Typography>
      </Box>
    );
  }

  // ---- Main render ----
  return (
    <Box sx={{ width: '100%' }}>
      {/* Selection info */}
      {(minChoices || maxChoices) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
          <Chip
            size="small"
            label={`${selectedSet.size} selected`}
            color={
              minChoices && selectedSet.size < minChoices
                ? 'warning'
                : maxChoices && selectedSet.size >= maxChoices
                  ? 'success'
                  : 'default'
            }
          />
          {minChoices && <Chip size="small" label={`Min: ${minChoices}`} variant="outlined" />}
          {maxChoices && <Chip size="small" label={`Max: ${maxChoices}`} variant="outlined" />}
        </Box>
      )}

      {/* Playlist header + Apple Music auth */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap'
        }}
      >
        {playlistMeta && (
          <Typography variant="caption" color="text.secondary">
            {playlistMeta.name} • {playlistMeta.trackCount} songs
          </Typography>
        )}

        {/* Discrete Apple Music auth chip */}
        {isMusicKitReady && !isAuthorized && (
          <Tooltip title="Sign in with Apple Music for full-length playback" arrow>
            <Button
              size="small"
              variant="text"
              onClick={authorize}
              disabled={isAuthLoading}
              startIcon={<HeadphonesIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.7rem',
                color: 'text.secondary',
                px: 1,
                py: 0.25,
                minHeight: 0,
                '&:hover': { color: 'text.primary' }
              }}
            >
              Have an Apple Music account? Sign in for the full songs
            </Button>
          </Tooltip>
        )}
        {isAuthorized && (
          <Tooltip title="Sign out of Apple Music" arrow>
            <Chip
              size="small"
              icon={<HeadphonesIcon sx={{ fontSize: 14 }} />}
              deleteIcon={<LogoutIcon sx={{ fontSize: 14 }} />}
              onDelete={unauthorize}
              label="Full playback"
              color="success"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Track list */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          maxHeight: 420,
          overflowY: 'auto'
        }}
      >
        {tracks.map((track) => {
          const isSelected = selectedSet.has(track.id);
          const isCurrentlyPlaying = playingTrackId === track.id && isPlaying;
          const atMax = !!maxChoices && selectedSet.size >= maxChoices && !isSelected;
          const canPlay = playbackMode === 'full' || !!track.previewUrl;

          return (
            <Card
              key={track.id}
              variant="outlined"
              sx={{
                opacity: atMax ? 0.5 : 1,
                ...(isSelected && {
                  border: '2px solid',
                  borderColor: 'primary.main'
                })
              }}
            >
              <Box
                onClick={() => !atMax && handleToggle(track.id)}
                sx={{
                  cursor: atMax ? 'not-allowed' : 'pointer',
                  '&:hover': !atMax ? { bgcolor: 'action.hover' } : undefined
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    '&:last-child': { pb: 1 }
                  }}
                >
                  {/* Checkbox */}
                  <Checkbox checked={isSelected} disabled={atMax} size="small" sx={{ p: 0 }} tabIndex={-1} />

                  {/* Artwork */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      overflow: 'hidden',
                      flexShrink: 0,
                      bgcolor: 'grey.800',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    {track.artwork ? (
                      <img src={track.artwork.url} alt={track.name} width={48} height={48} style={{ objectFit: 'cover' }} />
                    ) : (
                      <MusicNoteIcon sx={{ color: 'grey.500', fontSize: 24 }} />
                    )}
                  </Box>

                  {/* Track info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? 600 : 400,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'left'
                      }}
                    >
                      {track.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        textAlign: 'left'
                      }}
                    >
                      {track.artistName}
                    </Typography>
                  </Box>

                  {/* Duration */}
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {formatDuration(track.durationMs)}
                  </Typography>

                  {/* Play button */}
                  {canPlay && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        playPause(track.id, track.previewUrl);
                      }}
                      sx={{
                        color: isCurrentlyPlaying ? 'primary.main' : 'text.secondary'
                      }}
                    >
                      {isCurrentlyPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                    </IconButton>
                  )}
                </CardContent>
              </Box>
            </Card>
          );
        })}
      </Box>

      {/* Hidden ReactPlayer for preview-only playback */}
      {playbackMode === 'preview' && previewTrack?.previewUrl && (
        <ReactPlayer
          src={previewTrack.previewUrl}
          playing={isPlaying}
          width={0}
          height={0}
          onEnded={handlePreviewEnded}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

export default AppleMusicPlaylistField;
